import type { RankUpdate } from "@productlane/lib/src/roadmap-custom-order"
import {
	nextGroupOrder,
	planCustomRanks,
	roadmapGroupKey,
	sortByCustomOrder,
} from "@productlane/lib/src/roadmap-custom-order"

import type { RoadmapStatusGroup } from "./status-groups"

export type RoadmapRowType = "project" | "issue"

export interface RoadmapDragRow {
	kind: "row"
	listId: string
	rowType: RoadmapRowType
	itemId: string
}

export interface RoadmapDragGroup {
	kind: "group"
	groupKey: string
	label: string
}

export type RoadmapDragData = RoadmapDragRow | RoadmapDragGroup

// A project can sit under several initiatives, so a row's sortable id carries
// its list: dnd-kit needs every id in one DndContext to be unique.
export function roadmapListId(
	rowType: RoadmapRowType,
	label: string,
	roadmapId: string | null = null,
): string {
	return `${rowType}|${label}|${roadmapId ?? ""}`
}

export function sortableRowId(listId: string, itemId: string): string {
	return `${listId}::${itemId}`
}

/** The key the portal groups this header under, so both orders agree. */
export function statusGroupKey(group: RoadmapStatusGroup): string {
	const project = group.projects[0]
	if (project) return roadmapGroupKey(project.state, project.stateName)
	const issue = group.issues[0]
	if (issue) return roadmapGroupKey(issue.status, issue.statusName)
	return roadmapGroupKey(null, group.label)
}

export function dragScope(data: RoadmapDragData | undefined): string | null {
	if (!data) return null
	return data.kind === "group" ? "group" : data.listId
}

/** `rows` is the whole loaded list, which can be longer than what is rendered. */
export function planRowDrop(
	rows: readonly { id: string; portalSortOrder?: number | null }[],
	activeId: string,
	overId: string,
): RankUpdate[] {
	if (activeId === overId) return []
	const from = rows.findIndex((row) => row.id === activeId)
	const to = rows.findIndex((row) => row.id === overId)
	if (from === -1 || to === -1) return []
	return planCustomRanks(
		rows.map((row) => ({ id: row.id, rank: row.portalSortOrder })),
		activeId,
		to,
	)
}

export function planGroupDrop(
	visibleKeys: readonly string[],
	stored: readonly string[],
	activeKey: string,
	overKey: string,
): string[] | null {
	if (activeKey === overKey) return null
	const from = visibleKeys.indexOf(activeKey)
	const to = visibleKeys.indexOf(overKey)
	if (from === -1 || to === -1) return null
	const next = [...visibleKeys]
	next.splice(from, 1)
	next.splice(to, 0, activeKey)
	return nextGroupOrder(stored, next)
}

export function sortableGroupId(label: string): string {
	return `group::${label}`
}

export const GRIP_ATTRIBUTE = "data-reorder-grip"

interface PendingRow {
	id: string
	portalSortOrder?: number | null
	totalScore: number
}

// Ranks from a drop the store has not echoed yet, so the list shows the drop
// on the frame the drag ends instead of snapping back first.
export function withPendingRanks<T extends PendingRow>(
	rows: T[],
	pending: ReadonlyMap<string, number>,
): T[] {
	if (pending.size === 0) return rows
	return sortByCustomOrder(
		rows.map((row) => {
			const rank = pending.get(row.id)
			return rank === undefined ? row : { ...row, portalSortOrder: rank }
		}),
		(row) => row.portalSortOrder,
		(row) => row.totalScore,
	)
}

/** Same map back when nothing settled, so the state update bails out. */
export function prunePendingRanks(
	pending: ReadonlyMap<string, number>,
	rows: readonly { id: string; portalSortOrder?: number | null }[],
): ReadonlyMap<string, number> {
	if (pending.size === 0) return pending
	const next = new Map(pending)
	for (const row of rows) {
		if (next.get(row.id) === row.portalSortOrder) next.delete(row.id)
	}
	return next.size === pending.size ? pending : next
}
